import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const STATUS_OPTIONS = ['open', 'acknowledged', 'resolved'];

const statusBadge = (status) => {
  const colours = {
    open: { bg: '#ffc107', color: '#212529' },
    acknowledged: { bg: '#599BE3', color: '#fff' },
    resolved: { bg: '#91CF9B', color: '#212529' },
  };
  const s = colours[status] || colours.open;
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '0.78rem',
        fontWeight: 600,
        backgroundColor: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const typeBadge = (type) => {
  const isBug = type === 'bug';
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '0.78rem',
        fontWeight: 600,
        backgroundColor: isBug ? '#dc3545' : '#072040',
        color: '#fff',
      }}
    >
      {isBug ? 'Bug' : 'Feedback'}
    </span>
  );
};

const FeedbackManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to the still-open work: open + acknowledged (hides resolved).
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editNotes, setEditNotes] = useState({});
  const [saving, setSaving] = useState({});

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // 'active' = open + acknowledged. The API filters a single status at a time, so fetch
      // all and narrow client-side; a specific status is passed straight through.
      if (statusFilter && statusFilter !== 'active') params.status_filter = statusFilter;
      if (typeFilter) params.type_filter = typeFilter;
      const res = await api.get('/api/feedback', { params });
      const data = statusFilter === 'active'
        ? res.data.filter((i) => i.status === 'open' || i.status === 'acknowledged')
        : res.data;
      setItems(data);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleUpdate = async (id, updates) => {
    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.put(`/api/feedback/${id}`, updates);
      setItems((prev) => prev.map((item) => (item.id === id ? res.data : item)));
    } catch (err) {
      console.error('Failed to update feedback:', err);
    } finally {
      setSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await api.delete(`/api/feedback/${id}`);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to delete feedback:', err);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      const item = items.find((i) => i.id === id);
      if (item) {
        setEditNotes((prev) => ({ ...prev, [id]: item.admin_notes || '' }));
      }
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Feedback &amp; Bug Reports</h5>
        <div className="d-flex gap-2">
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">Open &amp; Acknowledged</option>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="bug">Bug</option>
            <option value="feedback">Feedback</option>
          </select>
        </div>
      </div>
      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-4 text-muted">No submissions found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>User</th>
                  <th>Subject</th>
                  <th>App</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => toggleExpand(item.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {new Date(item.created_at).toLocaleDateString('en-AU')}
                      </td>
                      <td>{typeBadge(item.type)}</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {item.submitted_by_name}
                        <br />
                        <small className="text-muted">{item.submitted_by_email}</small>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{item.subject}</td>
                      <td style={{ fontSize: '0.85rem' }}>{item.app_name || '—'}</td>
                      <td>{statusBadge(item.status)}</td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={6} style={{ backgroundColor: '#f8f9fa' }}>
                          <div style={{ padding: '12px 8px' }}>
                            <div style={{ marginBottom: '12px' }}>
                              <strong>Description:</strong>
                              <p
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  marginTop: '4px',
                                  marginBottom: 0,
                                  fontSize: '0.9rem',
                                }}
                              >
                                {item.description}
                              </p>
                            </div>

                            <div className="d-flex gap-3 align-items-start">
                              <div style={{ flex: 1 }}>
                                <label
                                  className="form-label fw-bold"
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  Admin Notes
                                </label>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows={3}
                                  value={editNotes[item.id] ?? item.admin_notes ?? ''}
                                  onChange={(e) =>
                                    setEditNotes((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label
                                  className="form-label fw-bold"
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  Status
                                </label>
                                <select
                                  className="form-select form-select-sm"
                                  value={item.status}
                                  onChange={(e) =>
                                    handleUpdate(item.id, { status: e.target.value })
                                  }
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ alignSelf: 'flex-end', display: 'flex', gap: '6px' }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  disabled={saving[item.id]}
                                  onClick={() =>
                                    handleUpdate(item.id, {
                                      admin_notes: editNotes[item.id] ?? '',
                                    })
                                  }
                                >
                                  {saving[item.id] ? 'Saving...' : 'Save Notes'}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            {item.updated_at && (
                              <small className="text-muted mt-2 d-block">
                                Last updated:{' '}
                                {new Date(item.updated_at).toLocaleString('en-AU')}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackManager;
